import {
	AlertDialog,
	Checkbox,
	FormattedText,
	FormItem,
	InputWithMoreInfo,
	RichTextEditor,
	TextInput,
	useConfirmationDialog,
	ValidationError,
	useRichTextEditorIntermediateState,
	RichTextEditorIntermediateStateHandlers
} from "client/Components";
import { IFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	BrandingSettingsListItemModel,
	BrandingSettingsSaveModel
} from "client/Api/Branding/branding";
import { isNotNil } from "client/Misc/utils";

export type SettingsTabProps = {
	attachable: IFormHandler<BrandingSettingsSaveModel>;
	isDefault: boolean;
	defaultBranding: BrandingSettingsListItemModel | undefined;
	isCustomHomeButtonUrlEnabled: boolean;
	shouldRenderUrl: boolean;
	onShouldRenderUrlChange(value: boolean): void;
};

export const SettingsTab = (props: SettingsTabProps): ReactElement => {
	const {
		attachable,
		isDefault,
		defaultBranding,
		isCustomHomeButtonUrlEnabled,
		shouldRenderUrl,
		onShouldRenderUrlChange,
	} = props;

	const [shouldShowDefaultBrandingInfo, setShouldShowDefaultBrandingInfo] = useState<boolean>(false);
	const [isDefaultConfirmed, setIsDefaultConfirmed] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();
	const richTextEditorIntermediateState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();

	const changeIsDefaultHandler = useCallback(
		async (isChecked: boolean): Promise<void> => {
			if (
				isChecked
					&& isNotNil(defaultBranding)
					&& !isDefaultConfirmed
			) {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AnotherBrandingIsDefault"),
					children: (
						<FormattedText
							text={localize("_DefaultBrandingWillBeReplaced")}
							thingsToReplace={[
								{
									what: "{0}",
									filler: <strong>{defaultBranding.brandingName}</strong>
								}
							]}
						/>
					),
					confirmText: localize("_Ok"),
					cancelText: localize("_Cancel"),
					trackingId: "default-branding-confirmation-dialog"
				});

				if (result.wasSuccessful) {
					setIsDefaultConfirmed(true);
					attachable.for("isDefault")
						.reducer
						.setValue(true);
					attachable.validation
						.triggerInputFinished();
				}
			} else {
				attachable.for("isDefault")
						.reducer
						.setValue(isChecked);
				attachable.validation
						.triggerInputFinished();
			}
		},
		[
			attachable,
			defaultBranding,
			isDefaultConfirmed,
			openConfirmationDialog
		]
	);

	const showCustomUrlInfo = useCallback(
		(): void => {
			alertManager.showInfo(localize("_CustomHomeButtonUrlExplanation"));
		},
		[alertManager]
	);

	return (
		<>
			<FormItem>
				<InputWithMoreInfo
					onMoreInfo={() => setShouldShowDefaultBrandingInfo(true)}
					trackingId="branding-info-button"
				>
					<Checkbox
						text={localize("_DefaultBranding")}
						value={isDefault}
						onChange={changeIsDefaultHandler}
						trackingId={
							attachable.for("isDefault")
								.reducer
								.path
						}
					/>
					{shouldShowDefaultBrandingInfo && (
						<AlertDialog
							title={localize("_DefaultBranding")}
							showCloseButton
							onClose={() => setShouldShowDefaultBrandingInfo(false)}
							trackingId="branding-info-dialog"
						>
							{localize("_DefaultBrandingInfo")}
						</AlertDialog>
					)}
				</InputWithMoreInfo>
				{confirmationDialogElement}
				<ValidationError {...attachable.for("isDefault").attachToValidationError()} />
			</FormItem>
			<FormItem label={localize("_BrandingName")}>
				<TextInput {...attachable.for("brandingName").attachToTextInput()} />
				<ValidationError {...attachable.for("brandingName").attachToValidationError()} />
			</FormItem>
			<FormItem label={localize("_ProductName")}>
				<TextInput {...attachable.for("productName").attachToTextInput()} />
				<ValidationError {...attachable.for("productName").attachToValidationError()} />
			</FormItem>
			{isCustomHomeButtonUrlEnabled && (
				<>
					<FormItem label={localize("_EnableCustomHomeButtonUrl")}>
						<Checkbox
							value={shouldRenderUrl}
							onChange={onShouldRenderUrlChange}
							trackingId="show-custom-url"
						/>
					</FormItem>
					{shouldRenderUrl && (
						<FormItem label={localize("_CustomHomeButtonUrl")}>
							<InputWithMoreInfo
								onMoreInfo={showCustomUrlInfo}
								trackingId="branding-info-button"
							>
								<TextInput {...attachable.for("customHomeButtonUrl").attachToTextInput()} />
							</InputWithMoreInfo>
							<ValidationError {...attachable.for("customHomeButtonUrl").attachToValidationError()} />
						</FormItem>
					)}
				</>
			)}
			<FormItem label={localize("_SupportInformation")}>
				<RichTextEditor
					{...attachable.for("supportContactDetails")
						.attachToRichTextEditor(richTextEditorIntermediateState)}
				/>
				<ValidationError
					{...attachable.for("supportContactDetails")
						.attachToValidationError()
					}
				/>
			</FormItem>
		</>
	);
};
SettingsTab.displayName = "SettingsTab";